/**
 * MermaidDiagram — renders a ```mermaid``` fenced code block as an SVG.
 *
 * Mirrors the pattern used by <VidyaFigure /> at MarkdownRenderer.tsx:285:
 * the markdown code-block interceptor handles the `language-mermaid` class
 * and renders this component with the block body as `body`.
 *
 * Why Mermaid:
 *  - Industry-standard text DSL for flowcharts, sequence, state, class,
 *    pie, mindmap, timeline, journey, gantt diagrams.
 *  - LLMs emit Mermaid much more reliably than custom JSON DSLs (it's
 *    in their pretraining data via GitHub, Notion, etc.).
 *  - Mermaid v11 supports `look: 'handDrawn'` which internally uses
 *    rough.js — the SAME library we already use for vidya-figure — so
 *    diagrams match the existing hand-drawn aesthetic without extra work.
 *
 * Failure mode:
 *  - mermaid.parse() throws on invalid syntax. We catch and show a
 *    graceful inline error box (same look as vidya-equation fallback).
 *    The streaming proxy doesn't need to know about mermaid; render
 *    fully resolves client-side after the closing ``` arrives.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  body: string;
}

// Lazy import — mermaid is ~500KB gzipped, only load if a block appears
let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => {
      mod.default.initialize({
        startOnLoad: false,
        // Hand-drawn aesthetic — matches rough.js look used by VidyaFigure.
        // Falls back gracefully on older Mermaid versions that don't know
        // the `look` flag.
        look: 'handDrawn',
        theme: 'default',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        // Reduce padding so diagrams sit closer to surrounding prose
        flowchart: { padding: 10, htmlLabels: true },
        sequence: { actorMargin: 50 },
        // Suppress mermaid's own error rendering — we handle errors below
        // with our own styled fallback.
        suppressErrorRendering: true,
      } as unknown as Parameters<typeof mod.default.initialize>[0]);
      return mod.default;
    });
  }
  return mermaidPromise;
}

// Auto-quote labels and subgraph titles that contain special chars or
// non-ASCII. Mermaid's grammar uses [ ] ( ) { } as shape delimiters, so
// an unquoted label like `B[Water (H₂O)]` is parsed as id `B` + label
// `Water ` + nested rounded-rect `(H₂O)` which then dangles — boom,
// "Parse error … Expecting 'SQE', 'STADIUMEND', …".
//
// The narrator keeps emitting these unquoted despite multiple rounds
// of prompt strengthening. Since Mermaid is rendered entirely
// client-side (no server-side repair path), we sanitise here.
//
// The transformations:
//   1. id[label]   → id["label"]   when label has parens/colons/non-ASCII
//   2. id(label)   → id("label")   same condition
//   3. id{label}   → id{"label"}   same condition
//   4. subgraph TITLE  → subgraph "TITLE"   when title has special chars
//
// We DON'T touch:
//   • Already-quoted labels: id["..."], subgraph "..."
//   • Bare identifiers (no shape): A --> B
//   • HTML inside labels (<br/>, <b>, <i>) — mermaid supports them
//   • Double-shape forms ((( )))/((label)) — those are circles, handled
//     separately by the regex order
function _labelNeedsQuoting(label: string): boolean {
  const trimmed = label.trim();
  if (!trimmed) return false;
  // Already quoted (whole-string quotes)
  if (/^".*"$/.test(trimmed)) return false;
  // Has shape-delimiter chars OR non-ASCII OR colons / semicolons /
  // angle brackets (other than HTML tags we want to preserve).
  // We allow `<` only when it's part of `<br`, `<i>`, `<b>` etc. —
  // those are mermaid-supported HTML and don't break parsing.
  if (/[(){}\[\]]/.test(trimmed)) return true;
  if (/[^\x00-\x7F]/.test(trimmed)) return true;
  if (/[;:](?!\s*\d)/.test(trimmed)) return true; // colon NOT followed by a digit (skips time labels like "12:00")
  return false;
}

function _escapeForMermaid(label: string): string {
  // Mermaid quoted strings are NOT JSON-style; backslashes don't
  // escape. The only thing we need to do is replace embedded double
  // quotes with `#quot;` (mermaid's HTML-entity escape).
  return label.replace(/"/g, '#quot;');
}

// Mermaid node-shape table. Each entry pairs an opening delimiter with its
// matching closer, plus the bracket pair to track depth inside the label
// (so a balanced inner pair like "(n, 2C)" doesn't get mistaken for the
// outer close). Order matters: 2-char openers MUST come before 1-char
// openers so longest-match wins when we scan for an opener.
//
// Mermaid supports more shapes than this (e.g. trapezoid `[/...\]`,
// asymmetric `>...]`), but they share openers with the listed shapes and
// would require disambiguating by the CLOSE token alone. The shapes here
// cover every variant we've seen in narrator output. Add new entries if
// the narrator reaches for a shape we haven't sanitised yet.
type ShapeDef = {
  open: string;
  close: string;
  depthChar: { open: string; close: string };
};

const _SHAPES: readonly ShapeDef[] = [
  // 2-char openers first
  { open: '[[', close: ']]', depthChar: { open: '[', close: ']' } }, // subroutine
  { open: '[(', close: ')]', depthChar: { open: '(', close: ')' } }, // cylinder
  { open: '([', close: '])', depthChar: { open: '[', close: ']' } }, // stadium
  { open: '((', close: '))', depthChar: { open: '(', close: ')' } }, // circle
  { open: '{{', close: '}}', depthChar: { open: '{', close: '}' } }, // hexagon
  // 1-char openers
  { open: '[', close: ']', depthChar: { open: '[', close: ']' } }, // rectangle
  { open: '(', close: ')', depthChar: { open: '(', close: ')' } }, // rounded
  { open: '{', close: '}', depthChar: { open: '{', close: '}' } }, // rhombus
];

// Lines that should be passed through unchanged — directives, comments,
// keywords. They don't contain node definitions and the walker's
// "identifier + opener" pattern could false-positive on them.
const _DIRECTIVE_PREFIXES = [
  '%%',
  'graph ',
  'flowchart ',
  'direction ',
  'classDef ',
  'class ',
  'style ',
  'linkStyle ',
  'click ',
];

function _isDirectiveLine(line: string): boolean {
  const trimmed = line.trimStart();
  if (!trimmed) return true;
  if (trimmed === 'end') return true; // closes a subgraph
  return _DIRECTIVE_PREFIXES.some((p) => trimmed.startsWith(p));
}

// Walk forward from `start`, tracking `shape.depthChar` balance, and
// return the index of the matching close delimiter (or -1 if none found
// on this line). The close is matched only when depth has returned to 0,
// so balanced inner pairs (e.g. "(n, 2C)" inside a circle) don't trick us.
function _findMatchingClose(
  line: string,
  start: number,
  shape: ShapeDef,
): number {
  let depth = 0;
  let i = start;
  while (i < line.length) {
    // Check for close BEFORE updating depth — the close char may itself be
    // the same as `depthChar.close`, and at depth 0 it's the outer close.
    if (depth === 0 && line.startsWith(shape.close, i)) return i;

    const c = line[i];
    if (c === shape.depthChar.open) depth++;
    else if (c === shape.depthChar.close && depth > 0) depth--;
    i++;
  }
  return -1;
}

// Process one Mermaid source line, finding every `identifier + opener`
// node definition on it and wrapping the label in quotes if it contains
// special chars. Edges, plain text, and chars not adjacent to an
// identifier are passed through unchanged.
function _sanitizeNodeLine(line: string): string {
  let out = '';
  let i = 0;
  while (i < line.length) {
    // Must be at a word boundary to start matching an identifier.
    if (i > 0 && /\w/.test(line[i - 1])) {
      out += line[i];
      i++;
      continue;
    }
    const idMatch = line.slice(i).match(/^([A-Za-z][A-Za-z0-9_]*)/);
    if (!idMatch) {
      out += line[i];
      i++;
      continue;
    }
    const id = idMatch[1];
    const afterId = i + id.length;

    const shape = _SHAPES.find((s) => line.startsWith(s.open, afterId));
    if (!shape) {
      out += line.slice(i, afterId);
      i = afterId;
      continue;
    }

    const labelStart = afterId + shape.open.length;
    const closePos = _findMatchingClose(line, labelStart, shape);
    if (closePos === -1) {
      // No matching close on this line — pass through, don't touch.
      out += line.slice(i, afterId);
      i = afterId;
      continue;
    }

    const label = line.slice(labelStart, closePos);
    const afterClose = closePos + shape.close.length;
    if (_labelNeedsQuoting(label)) {
      out += `${id}${shape.open}"${_escapeForMermaid(label)}"${shape.close}`;
    } else {
      out += line.slice(i, afterClose);
    }
    i = afterClose;
  }
  return out;
}

function sanitizeMermaidSource(src: string): string {
  if (!src) return src;

  return src
    .split('\n')
    .map((line) => {
      if (_isDirectiveLine(line)) return line;

      // Subgraph titles use different syntax (`subgraph TITLE` with no
      // bracket pair) and need their own pass.
      const subgraphMatch = line.match(/^(\s*subgraph\s+)([^\n"\[]+?)\s*$/);
      if (subgraphMatch) {
        const [, prefix, title] = subgraphMatch;
        const trimmed = title.trim();
        if (/\[/.test(trimmed)) return line; // bracketed-title form
        if (_labelNeedsQuoting(trimmed)) {
          return `${prefix}"${_escapeForMermaid(trimmed)}"`;
        }
        return line;
      }

      return _sanitizeNodeLine(line);
    })
    .join('\n');
}

// Each rendered diagram needs a stable unique id for mermaid's internal
// id-based addressing. We hash the body to keep it stable across re-renders
// of the same content (useful during streaming when React may re-render
// the parent while diagram body content is identical).
function hashBody(body: string): string {
  let h = 0;
  for (let i = 0; i < body.length; i++) {
    h = (h << 5) - h + body.charCodeAt(i);
    h |= 0;
  }
  return `mermaid-${Math.abs(h).toString(36)}`;
}

export function MermaidDiagram({ body }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const handleExpand = useCallback(() => setExpanded(true), []);
  const handleClose = useCallback(() => setExpanded(false), []);

  // Esc closes the modal (mirrors PlotRenderer + VidyaSvg).
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, handleClose]);

  useEffect(() => {
    let cancelled = false;
    const id = hashBody(body);
    // Pre-process the source to auto-quote labels that contain special
    // chars or non-ASCII. The narrator keeps ignoring the Mermaid label-
    // quoting rules despite ~5 rounds of prompt strengthening, and
    // Mermaid is rendered entirely client-side (no server repair path),
    // so we sanitise here deterministically. See `sanitizeMermaidSource`
    // below.
    const source = sanitizeMermaidSource(body.trim());

    if (!source) {
      setError('Empty diagram');
      return;
    }

    (async () => {
      try {
        const mermaid = await loadMermaid();
        // Validate syntax BEFORE rendering — mermaid.parse() throws on
        // syntax errors with a clearer message than mermaid.render() does.
        await mermaid.parse(source);
        if (cancelled) return;
        const { svg: rendered } = await mermaid.render(id, source);
        if (cancelled) return;
        setSvg(rendered);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : String(err ?? 'unknown error');
        setError(message);
        setSvg(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [body]);

  if (error) {
    // Graceful inline fallback — matches the visual style of the
    // vidya-equation degraded mode in MarkdownRenderer.tsx:308-318.
    return (
      <div className="my-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
        <div className="font-medium text-amber-800 dark:text-amber-200 mb-2">
          Diagram couldn't be rendered. Source:
        </div>
        <pre className="overflow-x-auto text-xs text-slate-700 dark:text-slate-300">
          {body}
        </pre>
        <div className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {error.slice(0, 160)}
        </div>
      </div>
    );
  }

  if (!svg) {
    // While loading mermaid module + rendering. Same height as a small
    // diagram so the layout doesn't jump when the SVG arrives.
    return (
      <div className="my-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4 h-32 flex items-center justify-center text-xs text-slate-400">
        Rendering diagram…
      </div>
    );
  }

  return (
    <>
      {/* Inline preview — clickable to expand. Same pattern as
          PlotRenderer + VidyaSvg: the whole panel is one click target
          that opens a portal modal. `block w-full p-0 text-left`
          forces button into block context so the child mermaid SVG
          gets a real container width to render into. */}
      <button
        type="button"
        onClick={handleExpand}
        className="group my-4 block w-full relative rounded-lg border border-slate-200
                   dark:border-slate-700 bg-white cursor-zoom-in
                   hover:border-slate-400 hover:shadow-md transition-all p-0 text-left"
        aria-label="Click to expand diagram to fullscreen"
        title="Click to expand"
      >
        <div
          ref={containerRef}
          className="mermaid-diagram bg-white flex justify-center p-2 pointer-events-none select-none"
          // The SVG is generated by mermaid and trusted (we control the source
          // via the LLM's mermaid output, which is text-DSL not user input).
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {/* Hover overlay — "⤢ Click to expand" badge on hover. */}
        <div className="absolute inset-0 flex items-center justify-center
                        bg-slate-900/0 group-hover:bg-slate-900/10
                        pointer-events-none transition-colors rounded-lg">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity
                           bg-white border border-slate-300 text-slate-700
                           text-xs font-medium px-3 py-1.5 rounded shadow">
            ⤢&nbsp; Click to expand
          </span>
        </div>
      </button>

      {/* Modal — portal to document.body for the same reason as
          PlotRenderer + VidyaSvg (parent transforms / will-change
          would otherwise trap a `position: fixed` descendant inside
          the chat panel). */}
      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-6"
            onClick={handleClose}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="relative bg-white rounded-lg shadow-2xl p-6 overflow-auto
                         w-[min(95vw,1200px)] max-h-[95vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center
                           text-slate-500 hover:text-slate-900 hover:bg-slate-100
                           text-2xl leading-none rounded transition-colors"
                aria-label="Close fullscreen diagram"
                title="Close (Esc)"
              >
                ×
              </button>
              <div
                className="mx-auto bg-white w-full"
                // Same trusted-source rationale as inline render.
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default MermaidDiagram;
